import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";

const TYPES = { phone: "Phone", office: "Office", in_person: "In person" };
const STATUS = { scheduled: "bg-sky-100 text-sky-800", completed: "bg-emerald-100 text-emerald-800", cancelled: "bg-slate-100 text-slate-600", no_show: "bg-red-100 text-red-700" };
const when = (a) => (a.at ? new Date(a.at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : a.time_text || "Time not set");

const Appt = ({ a, admin, reload }) => {
  const set = async (status) => {
    try { await api.put(`/surety/appointments/${a.id}`, { status }); toast.success("Appointment updated"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="flex flex-col gap-2 border-t border-slate-100 py-4 text-sm md:flex-row md:items-center" data-testid={`surety-appt-${a.id}`}>
      <div className="md:w-56"><div className="font-semibold text-slate-900" data-testid={`surety-appt-when-${a.id}`}>{when(a)}</div>
        {!a.at && a.time_text && <div className="text-xs text-amber-700">Couldn't read the exact time - confirm with the contractor</div>}</div>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900">{a.lead_name}{a.business ? ` · ${a.business}` : ""}</div>
        <div className="text-xs text-slate-500">{TYPES[a.type] || a.type}{a.address ? ` · ${a.address}` : ""}{a.phone ? ` · ${a.phone}` : ""}{admin && a.agent_name ? ` · ${a.agent_name}` : ""}</div>
        {a.bond_need && <div className="text-xs text-slate-600">Bond need: {a.bond_need}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge className={`border-0 capitalize ${STATUS[a.status] || ""}`} data-testid={`surety-appt-status-${a.id}`}>{a.status.replace("_", " ")}</Badge>
        {a.reminded && <Badge variant="outline">Reminders sent</Badge>}
        {a.status === "scheduled" && ["completed", "no_show", "cancelled"].map((s) => (
          <Button key={s} size="sm" variant="outline" className="h-7 capitalize" onClick={() => set(s)} data-testid={`surety-appt-${s}-${a.id}`}>{s.replace("_", " ")}</Button>
        ))}
      </div>
    </div>
  );
};

export default function SuretyAppointments() {
  const [d, setD] = useState(null);
  const load = () => api.get("/surety/appointments").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  const [admin, setAdmin] = useState(false);
  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setAdmin(r.data.role === "admin")).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const now = new Date().toISOString();
  const upcoming = (d?.appointments || []).filter((a) => a.status === "scheduled" && (!a.at || a.at >= now));
  const past = (d?.appointments || []).filter((a) => !upcoming.includes(a)).reverse();
  return (
    <div data-testid="surety-appointments-page">
      <PageHeader title="Appointments" subtitle="Every appointment Ava books. You and the contractor get a call and a text about 1 hour before each one." />
      {[["Upcoming", upcoming], ["Past", past]].map(([title, list]) => (
        <Card key={title} className="mb-5 p-6" data-testid={`surety-appts-${title.toLowerCase()}`}>
          <h3 className="mb-2 flex items-center gap-2 font-display font-bold text-slate-900"><CalendarClock className="h-4 w-4" />{title} ({list.length})</h3>
          {d && !list.length && <p className="text-sm text-slate-500">No {title.toLowerCase()} appointments.</p>}
          {list.map((a) => <Appt key={a.id} a={a} admin={admin} reload={load} />)}
        </Card>
      ))}
    </div>
  );
}
